/**
 * Markdown formatting, shared by the composers and the renderer.
 *
 * Posts are stored as the plain text the author typed — `contentFilter` derives
 * the markup at read time — so formatting is real characters in the field, not
 * editor state. `LIST_ITEM` is the grammar both halves agree on: the renderer
 * scans for it, and everything here writes it.
 *
 * Pure string math, no DOM and no React, so web's rich editor and mobile's
 * `<TextInput multiline>` drive the same behaviour.
 */

// A markdown list line: indent, a bullet (-, *, +) or a number (1. / 1)), then
// at least one space. The trailing space is what keeps `*emphasis*` and a
// `-5 degrees` measurement out of the list.
export const LIST_ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])[ \t]+(.*)$/;
// A list line whose body is empty — the "press Enter again to get out" case.
const EMPTY_LIST_ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])[ \t]*$/;
// The step a composer types, and the width a tab counts as when the renderer
// measures an indent. The renderer nests on any deeper indent, so this only has
// to be what gets written, not everything it will accept.
export const INDENT_COLUMNS = 2;
const INDENT = ' '.repeat(INDENT_COLUMNS);
// Undo one INDENT, or the tab someone used in its place.
const OUTDENT = new RegExp(`^(\\t| {1,${INDENT_COLUMNS}})`);

/** A replacement for the whole field plus where to leave the caret. */
export type TextEdit = {
  value: string;
  selectionStart: number;
  selectionEnd: number;
};

/** What a text field hands a formatting tool. */
export type Field = {
  value: string;
  selectionStart: number;
  selectionEnd: number;
};

const lineStartAt = (value: string, index: number): number =>
  value.lastIndexOf('\n', index - 1) + 1;

const lineEndAt = (value: string, index: number): number => {
  const next = value.indexOf('\n', index);
  return next === -1 ? value.length : next;
};

/** The next marker in sequence: `-` stays `-`, `3.` becomes `4.`. */
const nextMarker = (marker: string): string => {
  const number = Number.parseInt(marker, 10);
  if (Number.isNaN(number)) return marker;
  return `${number + 1}${marker.slice(-1)}`;
};

/**
 * Enter inside a list item — the behaviour Trello has: carry the list on to the
 * next line, and let a second Enter on the empty item step back out.
 *
 * Returns `null` when the caret is not in a list item, so the caller leaves the
 * keystroke alone.
 */
export const continueListOnEnter = (
  value: string,
  selectionStart: number,
  selectionEnd: number
): TextEdit | null => {
  // A selection that spans lines is a replace, not a continue.
  if (selectionStart !== selectionEnd) return null;

  const start = lineStartAt(value, selectionStart);
  const line = value.slice(start, lineEndAt(value, selectionStart));

  const empty = line.match(EMPTY_LIST_ITEM);
  if (empty) {
    // Step out one level, or drop the marker entirely at the top level. No
    // newline either way: the point is to stop making list items.
    const indent = empty[1] ?? '';
    const outdented =
      indent.length >= INDENT_COLUMNS
        ? `${indent.slice(INDENT_COLUMNS)}${empty[2] ?? '-'} `
        : '';
    const caret = start + outdented.length;
    return {
      value:
        value.slice(0, start) + outdented + value.slice(start + line.length),
      selectionStart: caret,
      selectionEnd: caret
    };
  }

  const item = line.match(LIST_ITEM);
  if (!item) return null;

  const prefix = `\n${item[1] ?? ''}${nextMarker(item[2] ?? '-')} `;
  const caret = selectionStart + prefix.length;
  return {
    value:
      value.slice(0, selectionStart) + prefix + value.slice(selectionStart),
    selectionStart: caret,
    selectionEnd: caret
  };
};

/** The lines the selection touches, as `[start, end]` offsets into `value`. */
const selectedLineRange = (
  value: string,
  selectionStart: number,
  selectionEnd: number
): [number, number] => [
  lineStartAt(value, selectionStart),
  lineEndAt(value, selectionEnd)
];

/**
 * Rewrite every selected line through `rewrite`.
 *
 * A range selection stays over the same lines, so a caller can indent the same
 * block twice in a row. A plain caret instead keeps its place in the text — it
 * must not end up selecting the line, or the next character typed would replace
 * the very prefix that was just added.
 */
const rewriteSelectedLines = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  rewrite: (line: string) => string
): TextEdit => {
  const [start, end] = selectedLineRange(value, selectionStart, selectionEnd);
  const lines = value.slice(start, end).split('\n');
  const rewritten = lines.map(rewrite);
  const nextValue =
    value.slice(0, start) + rewritten.join('\n') + value.slice(end);

  if (selectionStart === selectionEnd) {
    // Only line prefixes change here, so the caret's distance from the end of
    // its own line is what stays fixed.
    let lineStart = start;
    let shift = 0;
    for (let i = 0; i < lines.length; i += 1) {
      const before = lines[i] ?? '';
      const after = rewritten[i] ?? '';
      const lineEnd = lineStart + before.length;
      if (selectionStart <= lineEnd) {
        const caret =
          lineStart + shift + after.length - (lineEnd - selectionStart);
        return { value: nextValue, selectionStart: caret, selectionEnd: caret };
      }
      shift += after.length - before.length;
      lineStart = lineEnd + 1;
    }
  }

  return {
    value: nextValue,
    selectionStart: start,
    selectionEnd: start + rewritten.join('\n').length
  };
};

/**
 * Tab / Shift+Tab, or the indent buttons: one nesting level in or out. Returns
 * `null` when no selected line is a list item, so a caller can let Tab keep
 * moving focus everywhere else.
 */
export const indentListItems = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  outdent: boolean
): TextEdit | null => {
  const [start, end] = selectedLineRange(value, selectionStart, selectionEnd);
  const lines = value.slice(start, end).split('\n');
  if (!lines.some((line) => LIST_ITEM.test(line))) return null;

  return rewriteSelectedLines(value, selectionStart, selectionEnd, (line) => {
    const item = line.match(LIST_ITEM);
    if (!item) return line;
    const indent = item[1] ?? '';
    if (!outdent) return INDENT + line;
    return indent.length > 0 ? line.replace(OUTDENT, '') : line;
  });
};

/**
 * The bullet / numbered button: turn the selected lines into list items, or
 * back into plain lines when they already are that kind of list.
 */
export const toggleListOnSelection = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  ordered: boolean
): TextEdit => {
  const [start, end] = selectedLineRange(value, selectionStart, selectionEnd);
  const lines = value.slice(start, end).split('\n');
  const items = lines.map((line) => line.match(LIST_ITEM));

  // Only a block that is already entirely this kind of list toggles off;
  // anything else (plain lines, the other kind, a mix) becomes one.
  const isThisList = items.every(
    (item, index) =>
      lines[index]?.trim() === '' ||
      (item !== null && /\d/.test(item[2] ?? '') === ordered)
  );
  const hasItem = items.some((item) => item !== null);

  let number = 0;
  return rewriteSelectedLines(value, selectionStart, selectionEnd, (line) => {
    if (line.trim() === '') return line;
    const item = line.match(LIST_ITEM);
    if (isThisList && hasItem) return `${item?.[1] ?? ''}${item?.[3] ?? ''}`;
    number += 1;
    const marker = ordered ? `${number}.` : '-';
    return item
      ? `${item[1] ?? ''}${marker} ${item[3] ?? ''}`
      : `${marker} ${line}`;
  });
};

/** The inline marks a toolbar can toggle, and the delimiter each wraps in. */
const INLINE_MARKS = {
  bold: '**',
  italic: '*',
  strike: '~~',
  code: '`'
} as const;

type InlineMark = keyof typeof INLINE_MARKS;

/**
 * The bold/italic/strike/code button: wrap the selection, or unwrap it when it
 * is already wrapped. With nothing selected it drops in an empty pair and puts
 * the caret between them, so the next thing typed comes out marked.
 */
export const toggleInlineMark = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  mark: InlineMark
): TextEdit => {
  const delimiter = INLINE_MARKS[mark];
  const width = delimiter.length;
  const selected = value.slice(selectionStart, selectionEnd);

  // Already wrapped, either inside the selection or just outside it.
  const wrappedInside =
    selected.length >= width * 2 &&
    selected.startsWith(delimiter) &&
    selected.endsWith(delimiter);
  const wrappedOutside =
    value.slice(selectionStart - width, selectionStart) === delimiter &&
    value.slice(selectionEnd, selectionEnd + width) === delimiter;

  if (wrappedInside) {
    const bare = selected.slice(width, -width);
    return {
      value: value.slice(0, selectionStart) + bare + value.slice(selectionEnd),
      selectionStart,
      selectionEnd: selectionStart + bare.length
    };
  }
  if (wrappedOutside) {
    return {
      value:
        value.slice(0, selectionStart - width) +
        selected +
        value.slice(selectionEnd + width),
      selectionStart: selectionStart - width,
      selectionEnd: selectionEnd - width
    };
  }

  const wrapped = `${delimiter}${selected}${delimiter}`;
  return {
    value: value.slice(0, selectionStart) + wrapped + value.slice(selectionEnd),
    // Empty selection: sit between the delimiters. Otherwise keep the same text
    // selected so a second tap undoes it.
    selectionStart: selectionStart + width,
    selectionEnd: selectionStart + width + selected.length
  };
};

/** A line prefix a toolbar can toggle: `> ` for quote, `# ` for a heading. */
const PREFIXED = {
  quote: /^[ \t]*>[ \t]?/,
  heading: /^#{1,3}[ \t]+/
} as const;

/**
 * The quote / heading button: put the prefix on every selected line, or strip
 * it when every non-blank line already has one.
 */
export const toggleLinePrefixOnSelection = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  block: 'quote' | 'heading'
): TextEdit => {
  const [start, end] = selectedLineRange(value, selectionStart, selectionEnd);
  const lines = value.slice(start, end).split('\n');
  const filled = lines.filter((line) => line.trim() !== '');
  const allPrefixed =
    filled.length > 0 && filled.every((line) => PREFIXED[block].test(line));
  const prefix = block === 'quote' ? '> ' : '# ';

  return rewriteSelectedLines(value, selectionStart, selectionEnd, (line) => {
    if (line.trim() === '') return line;
    if (allPrefixed) return line.replace(PREFIXED[block], '');
    // Strip the other block prefix first, so a line is never `> # both`.
    const bare = line.replace(PREFIXED.quote, '').replace(PREFIXED.heading, '');
    return prefix + bare;
  });
};

/**
 * The link button: wrap the selection as `[text](url)`. With a URL selected the
 * text slot is left empty and selected, otherwise the URL slot is.
 */
export const insertLink = (
  value: string,
  selectionStart: number,
  selectionEnd: number
): TextEdit => {
  const selected = value.slice(selectionStart, selectionEnd);
  const selectedIsUrl = /^https?:\/\/\S+$/.test(selected.trim());
  const text = selectedIsUrl ? '' : selected;
  const url = selectedIsUrl ? selected.trim() : 'https://';
  const inserted = `[${text}](${url})`;

  // Land on whichever half still needs typing.
  const caret = selectedIsUrl
    ? selectionStart + 1
    : selectionStart + text.length + 3;
  return {
    value:
      value.slice(0, selectionStart) + inserted + value.slice(selectionEnd),
    selectionStart: caret,
    selectionEnd: selectedIsUrl ? caret : caret + url.length
  };
};
